<script>
import BanobagiBannerCarousel from "@/components/banobagi/BanobagiBannerCarousel.vue";
import BanobagiMarketplacePanel from "@/components/banobagi/BanobagiMarketplacePanel.vue";
import BanobagiRightsCountdown from "@/components/banobagi/BanobagiRightsCountdown.vue";
import BanobagiStatsGrid from "@/components/banobagi/BanobagiStatsGrid.vue";
import MarketplaceConnectDialogContent from "@/components/dialog-content/MarketplaceConnectDialogContent.vue";
import homeBanner from "@/assets/images/banobagi-home-banner.png";
import { loadBanobagiRightsLedger } from "@/services/banobagiRightsService";
import { useDialog } from "@/services/dialogService";
import { createEmptyBanobagiRightsSummary } from "@/utils/banobagiRights";
import { markRaw } from "vue";

const RawMarketplaceConnectDialogContent = markRaw(
  MarketplaceConnectDialogContent
);

export default {
  name: "BanobagiHome",
  components: {
    BanobagiBannerCarousel,
    BanobagiMarketplacePanel,
    BanobagiRightsCountdown,
    BanobagiStatsGrid
  },
  inject: {
    openBanobagiTransactionDialog: {
      default: null
    }
  },
  setup() {
    const dialog = useDialog();
    return { dialog };
  },
  data() {
    return {
      homeBanner,
      channels: [],
      isLoadingMarketplace: false,
      marketplaceError: "",
      isMarketplaceDialogOpen: false,
      rightsSummary: createEmptyBanobagiRightsSummary(),
      isLoadingRights: false,
      rightsLoadError: false
    };
  },
  computed: {
    profile() {
      return this.$store.state.shortProfile || {};
    },
    displayConnectChannel() {
      return this.$store.state.theme.display_connect_channel == "1";
    }
  },
  created() {
    this.getMarketplaceChannels();
    this.getRightsSummary();
  },
  methods: {
    async getRightsSummary() {
      this.isLoadingRights = true;
      this.rightsLoadError = false;

      try {
        const ledger = await loadBanobagiRightsLedger(this.$axios, {
          month_ago: 0,
          all_flag: true,
          page: 1,
          pageSize: 99999
        });
        this.rightsSummary = ledger.summary;
      } catch (error) {
        this.rightsSummary = createEmptyBanobagiRightsSummary();
        this.rightsLoadError = true;
        this.logRightsLoadError(error);
      } finally {
        this.isLoadingRights = false;
      }
    },
    logRightsLoadError(error) {
      this.$logger({
        response: error?.response || {
          config: {
            url: "/api/v1/Transaction/list_customer_transaction",
            methods: "POST",
            data: JSON.stringify({
              month_ago: 0,
              all_flag: true,
              page: 1,
              pageSize: 99999
            })
          }
        },
        action: {
          action: "calculate banobagi rights summary",
          id: 3,
          note: error?.message || "Unable to calculate all transaction rights"
        }
      });
    },
    async getMarketplaceChannels() {
      if (!this.displayConnectChannel) return;

      this.isLoadingMarketplace = true;
      this.marketplaceError = "";
      try {
        const data = await this.$axios("/api/v1/User/get/marketplace");
        if (data.result == 1) {
          this.channels = this.normalizeChannels(data.detail || {});
          return;
        }

        this.channels = [];
        this.marketplaceError = this.$t("error_message");
        this.logMarketplaceLoadError(data.message || "Unexpected response");
      } catch (error) {
        this.channels = [];
        this.marketplaceError = this.$t("error_message");
        this.logMarketplaceLoadError(error.message, error.response);
      } finally {
        this.isLoadingMarketplace = false;
      }
    },
    logMarketplaceLoadError(note, response = null) {
      this.$logger({
        response: response || {
          config: {
            url: "/api/v1/User/get/marketplace",
            methods: "GET",
            data: null
          }
        },
        action: {
          action: "load banobagi marketplace channels",
          id: 3,
          note
        }
      });
    },
    normalizeChannels(detail) {
      const available = Array.isArray(detail.marketplace)
        ? detail.marketplace
        : [];
      const connected = Array.isArray(detail.connect_marketplace)
        ? detail.connect_marketplace
        : [];

      const channelIdentity = channel =>
        String(
          channel.salesChannelId ||
            channel.sale_channel_id ||
            channel.branchId ||
            channel.id ||
            channel.marketplace ||
            ""
        );

      const connectedById = new Map(
        connected.map(channel => [channelIdentity(channel), channel])
      );

      const normalized = available.map(channel => {
        const connectedChannel = connectedById.get(channelIdentity(channel));
        if (connectedChannel) {
          connectedById.delete(channelIdentity(channel));
          return {
            ...channel,
            ...connectedChannel,
            isConnected: connectedChannel.status !== 0
          };
        }

        return {
          ...channel,
          isConnected: connected.length === 0 ? Boolean(channel.status) : false
        };
      });

      connectedById.forEach(channel => {
        normalized.push({
          ...channel,
          isConnected: channel.status !== 0
        });
      });

      return normalized;
    },
    async handleChannel(channel) {
      if (this.isMarketplaceDialogOpen) return;

      const branchId = channel.branchId || channel.branch_id;
      const salesChannelId = channel.salesChannelId || channel.sale_channel_id;

      this.isMarketplaceDialogOpen = true;
      try {
        const result = await this.dialog.showCustom(
          RawMarketplaceConnectDialogContent,
          {
            initialBranchId: branchId || null,
            initialSalesChannelId: salesChannelId || null
          },
          {
            contentClass:
              "modal-qr-v2 modal-qr-v2--sheet marketplace-connect-overlay",
            width: "100%"
          }
        );

        if (result?.action === "connected") {
          await this.getMarketplaceChannels();
        }
      } catch (error) {
        this.$logger({
          response: error.response || {
            config: {
              url: "dialog/marketplace-connect",
              methods: "OPEN",
              data: JSON.stringify({ branchId, salesChannelId })
            }
          },
          action: {
            action: "open banobagi marketplace dialog",
            id: 3,
            note: error.message
          }
        });
        await this.dialog.showError({ text: this.$t("error_message") });
      } finally {
        this.isMarketplaceDialogOpen = false;
      }
    },
    handleTransaction() {
      if (this.openBanobagiTransactionDialog) {
        this.openBanobagiTransactionDialog({ source: "stats" });
      }
    }
  }
};
</script>

<template>
  <div class="banobagi-home" data-test="profile-banobagi-page">
    <img
      :src="homeBanner"
      class="banobagi-home__banner"
      alt="PP KRIT BETWEEN MY LIPS CONCERT PRESENTED BY CP"
      width="1000"
      height="487"
      fetchpriority="high"
      data-test="profile-home-banner-img"
    />

    <div class="banobagi-home-container" data-test="profile-banobagi-content">
      <BanobagiStatsGrid
        :summary="rightsSummary"
        :is-loading="isLoadingRights"
        :has-error="rightsLoadError"
        @open-transaction="handleTransaction"
      />

      <BanobagiMarketplacePanel
        v-if="displayConnectChannel"
        :channels="channels"
        :is-loading="isLoadingMarketplace"
        :error-message="marketplaceError"
        @select-channel="handleChannel"
        @retry="getMarketplaceChannels"
      />

      <BanobagiRightsCountdown />

      <BanobagiBannerCarousel
        :profile="profile"
        data-test-prefix="profile-banner-carousel"
      />
    </div>
  </div>
</template>

<style lang="scss" scoped>
.banobagi-home {
  position: relative;
  display: flex;
  flex: 1 1 auto;
  flex-direction: column;
  min-height: 0;

  // พื้นหลังเต็มจอและลอดใต้ bottom navigation ไฟล์ท้องฟ้าจริงยังไม่มี จึงใช้ gradient ไปก่อน
  &::before {
    content: "";
    position: fixed;
    inset: 0;
    z-index: -1;
    // background: linear-gradient(
    //   180deg,
    //   #1053dc 0%,
    //   #1b63e3 40%,
    //   #2975eb 68%,
    //   #478ff3 100%
    // );
  }
}

.banobagi-home__banner {
  display: block;
  width: 77%;
  height: auto;
  margin: 24px auto 16px;
}

.banobagi-home-container {
  position: relative;
  flex: 1 1 auto;
  padding-bottom: 16px;
}
</style>
